/**
 * Click-ID resolver — pure, runtime-agnostic utility.
 *
 * Many ad-trackers (ClickFlare, Voluum, Binom, RedTrack, ClickMagick, …) all
 * follow the same pattern: assign a `click_id` on the visitor's first ad-click
 * on the lander, then expect that id to flow end-to-end (lander → checkout →
 * thank-you) so they can match a server postback to the original visit.
 *
 * This module:
 *   - Resolves the click id from URL query params (preferred — survives the
 *     redirect chain) and first-party cookies (fallback — set by the tracker's
 *     lander script).
 *   - Publishes the resolved value to `window.TagadaPay.tracking` so merchant-
 *     authored postback snippets in `stepConfig.scripts` can read it without
 *     re-implementing URL/cookie scraping.
 *
 * No React, no SDK class dependencies — works from React provider, standalone
 * SDK, the studio builder, the external-tracker bundle, or a raw <script>.
 *
 * The lists are intentionally explicit (not regexes) so we never accidentally
 * promote a random param to a "click id" — they are the exact identifiers
 * documented by each tracker / ad platform.
 */

// -----------------------------------------------------------------------------
// Configuration — explicit allow-lists
// -----------------------------------------------------------------------------

/** URL query-param names ad trackers / ad platforms use for click ids.
 *
 * Order matters — the resolver returns the FIRST matching entry, so put
 * tracker-specific canonical names BEFORE generic fallbacks like
 * `click_id` or `clickid`. This is why ClickFlare's `cf_click_id` comes
 * before `click_id`, and why the per-tracker canonical token (`cid` for
 * Voluum, `rtkclickid` for RedTrack, `cmc_tid` for ClickMagick) comes
 * first within its own block.
 */
export const CLICK_ID_URL_PARAMS = Object.freeze([
  // Postback ad-trackers (most specific first)
  'cf_click_id', // ClickFlare canonical
  'cid', // Voluum canonical (also: ClickFlare receiving-side)
  'rtkclickid', // RedTrack canonical
  'cmc_tid', // ClickMagick canonical (replaces legacy #S2#)
  'cmc_id', // ClickMagick legacy
  'cmcid', // ClickMagick alt
  'clickid', // Generic affiliate-network token (Binom, RedTrack, Voluum, …)
  'click_id', // Generic snake_case (ClickFlare, ClickMagick, …)
  // Ad-platform native click ids
  'gclid', // Google Ads
  'gbraid', // Google Ads (iOS app)
  'wbraid', // Google Ads (web→app)
  'fbclid', // Meta
  'msclkid', // Microsoft Ads
  'ttclid', // TikTok
  'twclid', // X / Twitter
  'li_fat_id', // LinkedIn
  'epik', // Pinterest
  'dclid', // Display & Video 360
  'yclid', // Yandex
  'irclickid', // Impact
] as const);

/** Cookie names ad trackers store their click ids under.
 *
 * Same ordering rule as URL params: canonical first-party cookies first
 * (e.g. `rtkclickid-store` for RedTrack), legacy fallbacks last.
 */
export const CLICK_ID_COOKIES = Object.freeze([
  // ClickFlare
  'cf_click_id',
  'cfclid',
  // RedTrack — `rtkclickid-store` is the canonical first-party cookie
  // set by RedTrack's Universal Tracking Script.
  'rtkclickid-store',
  '_rtkclickid',
  'rtkclickid',
  // Voluum
  '_voluum',
  '_voluumclickid',
  // Binom
  '_binom',
  '_binomclickid',
  // ClickMagick
  '_mck',
  'cmcid',
  // Other
  'skro-click-id', // Skro
  'click_id', // generic catch-all (last)
] as const);

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type ClickIdSource = 'url' | 'cookie';

export interface ResolvedClickId {
  /** The first matching click id, or null. */
  clickId: string | null;
  /** Where it was sourced from. */
  source: ClickIdSource | null;
  /** Which key (param name or cookie name) matched. */
  key: string | null;
  /** Every click-id-shaped value found, namespaced as `url:foo` / `cookie:bar`. */
  all: Record<string, string>;
}

export interface TagadaTrackingGlobal extends ResolvedClickId {
  /** ms since epoch when the resolver last ran. */
  resolvedAt: number;
}

declare global {
  interface Window {
    TagadaPay?: {
      tracking?: TagadaTrackingGlobal;
      [key: string]: unknown;
    };
  }
}

// -----------------------------------------------------------------------------
// Pure resolution
// -----------------------------------------------------------------------------

function readCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

function readUrlParam(search: string, name: string): string | null {
  if (!search) return null;
  try {
    return new URLSearchParams(search).get(name);
  } catch {
    return null;
  }
}

/**
 * Resolve the visitor's click id. Pure — no side effects, safe in SSR.
 *
 * Resolution order:
 *   1. URL params (in CLICK_ID_URL_PARAMS order) — most authoritative.
 *   2. Cookies   (in CLICK_ID_COOKIES order)     — fallback.
 */
export function resolveClickId(): ResolvedClickId {
  const all: Record<string, string> = {};
  let clickId: string | null = null;
  let source: ClickIdSource | null = null;
  let key: string | null = null;

  const search = typeof window !== 'undefined' ? window.location?.search ?? '' : '';
  for (const name of CLICK_ID_URL_PARAMS) {
    const v = readUrlParam(search, name);
    if (v) {
      all[`url:${name}`] = v;
      if (clickId === null) {
        clickId = v;
        source = 'url';
        key = name;
      }
    }
  }

  for (const name of CLICK_ID_COOKIES) {
    const v = readCookie(name);
    if (v) {
      all[`cookie:${name}`] = v;
      if (clickId === null) {
        clickId = v;
        source = 'cookie';
        key = name;
      }
    }
  }

  return { clickId, source, key, all };
}

// -----------------------------------------------------------------------------
// Browser-side publication
// -----------------------------------------------------------------------------

/**
 * Resolve and merge tracking metadata into `window.TagadaPay.tracking`.
 *
 * Idempotent and namespace-safe: we only ever touch the `tracking` key, never
 * other namespaces (e.g. `order` set by the thank-you page).
 *
 * Returns the published value, or `null` when called outside a browser.
 */
export function publishTrackingGlobal(): TagadaTrackingGlobal | null {
  if (typeof window === 'undefined') return null;
  const resolved = resolveClickId();
  const tracking: TagadaTrackingGlobal = { ...resolved, resolvedAt: Date.now() };
  window.TagadaPay = { ...(window.TagadaPay ?? {}), tracking };
  return tracking;
}
